// src/services/TicketService.js

import API from './api';

// Ticket API Service
const TicketService = {
    // Create new ticket with optional file uploads
    createTicket: async (ticketData, files) => {
        try {
            // Create FormData if files are provided
            if (files && files.length > 0) {
                const formData = new FormData();

                // Append ticket data
                Object.keys(ticketData).forEach(key => {
                    if (key === 'assignedTo' && Array.isArray(ticketData[key])) {
                        formData.append(key, JSON.stringify(ticketData[key]));
                    } else {
                        formData.append(key, ticketData[key]);
                    }
                });

                // Append files
                files.forEach(file => {
                    formData.append('files', file);
                });

                const res = await API.post('/api/tickets', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return res.data;
            } else {
                // No files, send as regular JSON
                const res = await API.post('/api/tickets', ticketData);
                return res.data;
            }
        } catch (error) {
            console.error('Error creating ticket:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get all tickets with filtering, pagination, and sorting
    getAllTickets: async (params = {}) => {
        try {
            const res = await API.get('/api/tickets', { params });
            return res.data;
        } catch (error) {
            console.error('Error getting tickets:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get all tickets with filtering, pagination, and sorting
    getTicketsForClient: async (clientId) => {
        try {
            const res = await API.get(`/api/tickets/client/${clientId}`);
            return res.data;
        } catch (error) {
            console.error('Error getting tickets:', error.response?.data || error.message);
            throw error;
        }
    },

    

    // Get ticket by ID
    getTicketById: async (ticketId) => {
        try {
            const res = await API.get(`/api/tickets/${ticketId}`);
            return res.data;
        } catch (error) {
            console.error('Error getting ticket by ID:', error.response?.data || error.message);
            throw error;
        }
    },

    // Update ticket by ID with support for attachments
    // Update ticket by ID with support for attachments
    updateTicket: async (ticketId, ticketData, files = []) => {
        try {
            // If files are included, use FormData approach
            if (files && files.length > 0) {
                const formData = new FormData();

                // Append ticket data
                Object.keys(ticketData).forEach(key => {
                    if (key === 'assignedTo' || key === 'tags') {
                        formData.append(key, JSON.stringify(ticketData[key]));
                    } else {
                        formData.append(key, ticketData[key]);
                    }
                });

                // Append files
                files.forEach(file => {
                    formData.append('files', file);
                });

                const res = await API.put(`/api/tickets/${ticketId}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return res.data;
            } else {
                // Regular update without files
                const res = await API.put(`/api/tickets/${ticketId}`, ticketData);
                return res.data;
            }
        } catch (error) {
            console.error('Error updating ticket:', error.response?.data || error);
            if (error.response?.data?.error) {
                // More detailed error from server
                throw new Error(error.response.data.error);
            }
            throw error;
        }
    },

    // Add comment to a ticket with optional file uploads
    addComment: async (ticketId, commentData, files) => {
        try {
            // Create FormData if files are provided
            if (files && files.length > 0) {
                const formData = new FormData();
                formData.append('content', commentData.content);

                // Append files
                files.forEach(file => {
                    formData.append('files', file);
                });

                const res = await API.post(`/api/tickets/${ticketId}/comments`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                return res.data;
            } else {
                // No files, send as regular JSON
                const res = await API.post(`/api/tickets/${ticketId}/comments`, commentData);
                return res.data;
            }
        } catch (error) {
            console.error('Error adding comment:', error.response?.data || error.message);
            throw error;
        }
    },

    // Change ticket status
    updateTicketStatus: async (ticketId, status) => {
        try {
            const res = await API.patch(`/api/tickets/${ticketId}/status`, { status });
            return res.data;
        } catch (error) {
            console.error('Error updating ticket status:', error.response?.data || error.message);
            throw error;
        }
    },

    // Add attachments to a ticket
    addAttachments: async (ticketId, files) => {
        try {
            const formData = new FormData();

            // Append files
            files.forEach(file => {
                formData.append('files', file);
            });

            const res = await API.post(`/api/tickets/${ticketId}/attachments`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return res.data;
        } catch (error) {
            console.error('Error adding attachments:', error.response?.data || error.message);
            throw error;
        }
    },

    // Delete an attachment
    deleteAttachment: async (attachmentId) => {
        try {
            const res = await API.delete(`/api/tickets/attachments/${attachmentId}`);
            return res.data;
        } catch (error) {
            console.error('Error deleting attachment:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get all comments for a ticket
    getTicketComments: async (ticketId) => {
        try {
            const res = await API.get(`/api/tickets/${ticketId}/comments`);
            return res.data;
        } catch (error) {
            console.error('Error getting ticket comments:', error.response?.data || error.message);
            throw error;
        }
    },

    // Delete ticket by ID (Admin or Project Manager only)
    deleteTicket: async (ticketId) => {
        try {
            const res = await API.delete(`/api/tickets/${ticketId}`);
            return res.data;
        } catch (error) {
            console.error('Error deleting ticket:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get tickets by project ID
    getTicketsByProject: async (projectId, params = {}) => {
        try {
            const res = await API.get(`/api/tickets/by-project/${projectId}`, { params });
            return res.data;
        } catch (error) {
            console.error('Error getting project tickets:', error.response?.data || error.message);
            throw error;
        }
    },

    // Get ticket statistics for dashboard (Admin only)
    getTicketStatistics: async () => {
        try {
            const res = await API.get('/api/tickets/statistics/dashboard');
            return res.data;
        } catch (error) {
            console.error('Error getting ticket statistics:', error.response?.data || error.message);
            throw error;
        }
    }
};

export default TicketService;